/** The identity-preserving reload exchange used directly by CallRoute. */

import type { JoinGrant, ResumeRoomInput } from '@era/shared'
import {
  prepareGrantResume,
  readCreatorControl,
  stashGrant,
  type HeldGrant,
} from './livekit-handoff.js'

export type ResumeGrantPost = (
  input: ResumeRoomInput,
  bearer: string,
) => Promise<JoinGrant>

/**
 * Rotate one connected grant without ever falling through to normal join.
 * The caller supplies the same-origin POST transport; this function owns the
 * exact request, response invariants, creator merge, and durable handoff.
 */
export async function resumeStoredGrant(
  code: string,
  held: HeldGrant,
  post: ResumeGrantPost,
): Promise<HeldGrant> {
  const prepared = prepareGrantResume(code, held)
  const prior = prepared.grant
  const exchange = prepared.pending_resume
  if (!exchange) throw new Error('resume successor credentials were not prepared')

  const input: ResumeRoomInput = {
    resume_capability_id: prior.resume_capability_id,
    request_id: exchange.request_id,
    successor_resume_capability_id: exchange.successor_resume_capability_id,
    successor_resume_capability: exchange.successor_resume_capability,
    successor_quality_connection_id: exchange.successor_quality_connection_id,
    successor_quality_capability: exchange.successor_quality_capability,
  }
  const grant = await post(input, prior.resume_capability)
  if (
    grant.session_id !== prior.session_id ||
    grant.join_code !== prior.join_code ||
    grant.identity !== prior.identity ||
    grant.display_name !== prior.display_name ||
    grant.resume_capability_id !== exchange.successor_resume_capability_id ||
    grant.resume_capability !== exchange.successor_resume_capability ||
    grant.quality_connection_id !== exchange.successor_quality_connection_id ||
    grant.quality_token !== exchange.successor_quality_capability
  ) {
    throw new Error('the server returned a resume grant that did not preserve this participant safely')
  }

  // The creator secret was never sent. Merge it only from the old local grant
  // after the response proves it belongs to the same session/code/identity.
  const controlToken = readCreatorControl(code, grant.session_id)
  const resumed: HeldGrant = {
    grant: controlToken ? { ...grant, control_token: controlToken } : grant,
    connected: false,
  }
  stashGrant(resumed)
  return resumed
}
